import { CSSProperties, ElementType } from 'react';

import { PaletteType, ReckonTheme, useTheme } from '@reckon-web/theme';
import { OverflowStrategyType, useOverflowStrategy } from '@reckon-web/text';
import { forwardRefWithAs } from '@reckon-web/utils';

type ValidHeading = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type HeadingLevelType = keyof ReturnType<typeof getLevels>;

type HeadingProps = {
  /** The horizontal alignment. */
  align?: CSSProperties['textAlign'];
  /** The color of the text. */
  color?: keyof PaletteType['text'] | 'inherit';
  /** The leading of the heading. */
  leading?: keyof ReckonTheme['typography']['leading'];
  /** Manage how text behaves with regard to overflow. */
  overflowStrategy?: OverflowStrategyType;
  /** The heading level. */
  level: HeadingLevelType;
};

export const Heading = forwardRefWithAs<'h1', HeadingProps>(
  (
    {
      as: comp,
      color: colorKey = 'base',
      leading = 'base',
      level,
      overflowStrategy = 'wrap',
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const overflowStyles = useOverflowStrategy(overflowStrategy);

    const levels = getLevels(theme);
    const lvl: ValidHeading = `h${level}` as any;
    const Tag = comp ? comp : lvl;
    const ariaAttributes = getAriaAttributes(comp, level);

    const resolvedColor =
      colorKey === 'inherit' ? 'inherit' : theme.palette.text[colorKey];

    const styles = {
      color: resolvedColor,
      fontFamily: theme.typography.fontFamily.heading,
      fontSize: levels[level].size,
      fontWeight: levels[level].weight,
      lineHeight: theme.typography.leading[leading],
      textTransform: levels[level].transform,
      ...overflowStyles,
    };

    return (
      <Tag
        ref={ref}
        // @ts-ignore
        css={styles}
        {...ariaAttributes}
        {...props}
      />
    );
  }
);

// Utils
// ------------------------------

/**
 * Prefer native heading tags, but provide accessible attributes if the consumer
 * swaps out the underlying element.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/heading_role
 */

function getAriaAttributes(
  tag: ElementType<any> | undefined,
  level: HeadingLevelType
) {
  if (!tag) return null;

  return { role: 'heading', 'aria-level': level };
}

const getLevels = (theme: ReckonTheme) => {
  const { fontSize, fontWeight } = theme.typography;

  return {
    '1': {
      size: fontSize['xxxlarge'],
      weight: fontWeight.heavy,
      transform: 'none',
    },
    '2': {
      size: fontSize.xxlarge,
      weight: fontWeight.bold,
      transform: 'none',
    },
    '3': {
      size: fontSize.xlarge,
      weight: fontWeight.bold,
      transform: 'none',
    },
    '4': {
      size: fontSize.large,
      weight: fontWeight.bold,
      transform: 'none',
    },
    '5': {
      size: fontSize.medium,
      weight: fontWeight.bold,
      transform: 'none',
    },
    '6': {
      size: fontSize.small,
      weight: fontWeight.bold,
      transform: 'uppercase',
    },
  };
};
